import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { CContainer, CRow, CCol, CCard, CCardBody, CSpinner, CButton, CBadge, CCollapse } from '@coreui/react';
import api from '../../api/axiosInstance';
import MathRenderer from '../../components/MathRenderer';
import { FaFacebookF, FaWhatsapp, FaXTwitter } from 'react-icons/fa6';
import Latex from 'react-latex-next';
import 'katex/dist/katex.min.css';
import AnnouncementBanner from '../../components/AnnouncementBanner';

// PDF Packages
import html2pdf from 'html2pdf.js';
import renderMathInElement from 'katex/contrib/auto-render';

const ViewQuestion = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [questionData, setQuestionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showSolution, setShowSolution] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  const pdfTemplateRef = useRef(null);

  const handleToggleSolution = () => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      navigate('/login', { state: { from: location.pathname } });
      return;
    }
    setShowSolution(!showSolution);
  };

  const handleShare = (platform) => {
    const shareUrl = window.location.href;
    if (platform === 'whatsapp') {
      window.open(`https://api.whatsapp.com/send?text=Check out this question: ${encodeURIComponent(shareUrl)}`, '_blank');
    } else if (platform === 'facebook') {
      window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, '_blank');
    } else if (platform === 'twitter') {
      window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}`, '_blank');
    }
  };

  // Helper: Fetch image as Data URL via Blob (Bypasses Canvas CORS restrictions)
  const fetchImageDataUrl = async (url) => {
    try {
      const fullUrl = url.startsWith('http')
        ? url
        : `https://apiambipie.mohdwebsolution.in${url.startsWith('/') ? '' : '/'}${url}`;

      const response = await fetch(fullUrl);
      const blob = await response.blob();
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = () => resolve(fullUrl);
        reader.readAsDataURL(blob);
      });
    } catch (e) {
      console.warn("Fetch Blob image error:", e);
      return url;
    }
  };

  // Process raw HTML to ensure absolute URLs and proper PDF styles
  const preparePdfHtml = async (rawHtml) => {
    if (!rawHtml) return '';

    let text = String(rawHtml).replace(/\\\$|\$&bsol;\$/g, '$');
    const parser = new DOMParser();
    const doc = parser.parseFromString(text, 'text/html');

    const images = Array.from(doc.querySelectorAll('img'));
    for (let img of images) {
      let src = img.getAttribute('src');
      if (src) {
        const base64Data = await fetchImageDataUrl(src);
        img.setAttribute('src', base64Data);
        img.setAttribute('style', 'max-width: 100% !important; height: auto !important; display: block !important; margin: 12px auto !important;');
      }
    }

    return doc.body.innerHTML;
  };

  // PDF Export Handler
  const handleDirectDownloadPDF = async () => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      alert("Please login first to download the PDF.");
      navigate('/login', { state: { from: location.pathname } });
      return;
    }

    const containerWrapper = document.getElementById('pdf-render-wrapper');
    if (!pdfTemplateRef.current || downloadingPdf || !containerWrapper) return;

    try {
      setDownloadingPdf(true);
      const container = pdfTemplateRef.current;

      // 1. Convert and insert Question Text
      const qStmtEl = container.querySelector('#pdf-question-stmt');
      if (qStmtEl && questionData) {
        const parsedQuestionHtml = await preparePdfHtml(questionData.questionText || questionData.QuestionText);
        qStmtEl.innerHTML = parsedQuestionHtml;
      }

      // 2. Convert and insert Options
      const letters = ['A', 'B', 'C', 'D', 'E'];
      for (let letter of letters) {
        const optEl = container.querySelector(`#pdf-opt-${letter}`);
        const val = questionData[`option${letter}`] || questionData[`Option${letter}`];
        if (optEl && val) {
          const parsedOpt = await preparePdfHtml(val);
          optEl.innerHTML = parsedOpt;
        }
      }

      // 3. Convert and insert Solution
      const solEl = container.querySelector('#pdf-solution-body');
      if (solEl && (questionData.solution || questionData.Solution)) {
        const parsedSol = await preparePdfHtml(questionData.solution || questionData.Solution);
        solEl.innerHTML = parsedSol;
      }

      // 4. Render Math / KaTeX Formats
      if (typeof renderMathInElement === 'function') {
        renderMathInElement(container, {
          delimiters: [
            { left: '$$', right: '$$', display: true },
            { left: '$', right: '$', display: false },
            { left: '\\(', right: '\\)', display: false },
            { left: '\\[', right: '\\]', display: true }
          ],
          throwOnError: false
        });
      }

      // 5. Temporary visible mount on viewport for accurate html2canvas layout rendering
      containerWrapper.style.position = 'fixed';
      containerWrapper.style.top = '0px';
      containerWrapper.style.left = '0px';
      containerWrapper.style.zIndex = '99999';
      containerWrapper.style.visibility = 'visible';
      containerWrapper.style.opacity = '1';

      // Wait for all inline images to settle in DOM
      const imagesInPdf = Array.from(container.querySelectorAll('img'));
      await Promise.all(
        imagesInPdf.map((img) => {
          if (img.complete) return Promise.resolve();
          return new Promise((res) => {
            img.onload = res;
            img.onerror = res;
          });
        })
      );

      await new Promise((resolve) => setTimeout(resolve, 400));

      const customId = questionData?.customQuestionId || `Question_${id}`;
      const opt = {
        margin: [8, 8, 8, 8],
        filename: `${customId}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { 
          scale: 2, 
          useCORS: true, 
          allowTaint: true,
          logging: false,
          scrollY: 0,
          scrollX: 0
        },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
      };

      await html2pdf().set(opt).from(container).save();
    } catch (err) {
      console.error("PDF generation error:", err);
      alert("Failed to download PDF. Please try again.");
    } finally {
      if (containerWrapper) {
        containerWrapper.style.position = 'absolute';
        containerWrapper.style.top = '-99999px';
        containerWrapper.style.left = '-99999px';
        containerWrapper.style.visibility = 'hidden';
        containerWrapper.style.opacity = '0';
      }
      setDownloadingPdf(false);
    }
  };

  useEffect(() => {
    const fetchQuestionDetails = async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await api.get(`/api/Content/GetQuestionById/${id}`);
        setQuestionData(res.data);
      } catch (err) {
        console.error("Error fetching question details:", err);
        setError("Failed to load question details or question not found.");
      } finally {
        setLoading(false);
      }
    };

    if (id) fetchQuestionDetails();
  }, [id]);

  const renderContent = (rawHtml) => {
    if (!rawHtml || typeof rawHtml !== 'string') return '';
    const correctedHtml = rawHtml.replace(
      /src="\/uploads\//g,
      'src="https://apiambipie.mohdwebsolution.in/uploads/'
    );
    const mathRegex = /(\$\$[\s\S]+?\$\$|\$[\s\S]+?\$)/g;

    const renderMathText = (textStr) => {
      if (!textStr) return null;
      const parts = textStr.split(mathRegex);
      return parts.map((part, pIdx) => {
        if (!part) return null;
        if ((part.startsWith('$') && part.endsWith('$')) || (part.startsWith('$$') && part.endsWith('$$'))) {
          return <Latex key={pIdx}>{part}</Latex>;
        }
        return <span key={pIdx}>{part}</span>;
      });
    };

    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(correctedHtml, 'text/html');
      const childNodes = Array.from(doc.body.childNodes);

      return (
        <div className="view-question-detail-preview ck-content" style={{ lineHeight: '1.7' }}>
          {childNodes.map((node, idx) => {
            if (node.nodeName === 'IMG' || (node.querySelector && node.querySelector('img'))) {
              const imgTag = node.nodeName === 'IMG' ? node : node.querySelector('img');
              let imgSrc = imgTag.getAttribute('src') || '';
              return (
                <div key={idx} className="my-3 text-center p-2 border bg-light rounded d-block overflow-hidden" style={{ maxWidth: '100%', width: '100%', margin: '15px auto' }}>
                  <img src={imgSrc} alt="Academic Visual" style={{ maxHeight: '400px', maxWidth: '100%', width: 'auto', height: 'auto', objectFit: 'contain', borderRadius: '4px' }} />
                </div>
              );
            }
            const textVal = node.textContent || '';
            if (mathRegex.test(textVal)) {
              return <div key={idx} style={{ margin: '6px 0', width: '100%' }}>{renderMathText(textVal)}</div>;
            }
            return <div key={idx} dangerouslySetInnerHTML={{ __html: node.outerHTML || node.textContent }} style={{ display: 'inline-block', width: '100%' }} />;
          })}
        </div>
      );
    } catch (err) {
      return <Latex>{correctedHtml}</Latex>;
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '70vh' }}>
        <CSpinner color="primary" size="lg" />
      </div>
    );
  }

  if (error || !questionData) {
    return (
      <CContainer className="text-center mt-5 py-5">
        <h4 className="text-danger mb-3">{error || "Data load error"}</h4>
        <CButton color="primary" onClick={() => navigate(-1)}>Go Back</CButton>
      </CContainer>
    );
  }

  const isSubjectiveMode = (questionData.questionType || questionData.QuestionType) === 'Subjective';

  const activeOptions = [
    { key: 'optionA', label: 'A', value: questionData.optionA || questionData.OptionA },
    { key: 'optionB', label: 'B', value: questionData.optionB || questionData.OptionB },
    { key: 'optionC', label: 'C', value: questionData.optionC || questionData.OptionC },
    { key: 'optionD', label: 'D', value: questionData.optionD || questionData.OptionD }
  ];

  const optE = questionData.optionE || questionData.OptionE;
  if (optE && optE.toString().trim() !== '') {
    activeOptions.push({ key: 'optionE', label: 'E', value: optE });
  }

  return (
    <CContainer className="py-4">
      {/* Top Header Bar */}
      <div className="d-flex justify-content-between align-items-center p-3 text-white rounded-top shadow-sm" style={{ backgroundColor: 'rgb(0, 33, 71)' }}>
        <div className="d-flex align-items-center gap-3">
          <h4 className="m-0 fw-bold" style={{ color: 'yellow' }}>Question Details</h4>
          <CBadge className="p-2" style={{ fontSize: '0.85rem', color:'white', backgroundColor: 'rgba(255, 255, 255, 0.1)', borderRadius: '6px' }}>
            ID: {questionData.customQuestionId || `NSRN01000${id}`}
          </CBadge>
        </div>
      </div>

      {/* Syllabus Metadata Track */}
      <div className="bg-white border-bottom p-3 shadow-sm mb-4">
        <div className="text-muted small fw-bold mb-3 d-flex align-items-center gap-2">
          📌 ACADEMIC SYLLABUS METADATA TRACK
        </div>
        <CRow className="g-3 text-center">
          <CCol xs={6} sm={4} md={3} lg={2}>
            <div className="border p-2 rounded bg-light shadow-xs h-100">
              <div className="text-muted small text-uppercase fw-bold mb-1" style={{ fontSize: '11px' }}>Chapter</div>
              <div className="fw-bold text-primary text-truncate px-1" style={{ fontSize: '13px' }}>{questionData.chapterName || 'N/A'}</div>
            </div>
          </CCol>
          <CCol xs={6} sm={4} md={3} lg={2}>
            <div className="border p-2 rounded bg-light shadow-xs h-100">
              <div className="text-muted small text-uppercase fw-bold mb-1" style={{ fontSize: '11px' }}>Topic</div>
              <div className="fw-bold text-dark text-truncate px-1" style={{ fontSize: '13px' }}>{questionData.topicName || 'N/A'}</div>
            </div>
          </CCol>
          <CCol xs={6} sm={4} md={3} lg={2}>
            <div className="border p-2 rounded bg-light shadow-xs h-100">
              <div className="text-muted small text-uppercase fw-bold mb-1" style={{ fontSize: '11px' }}>Asked In</div>
              <div className="fw-bold text-info text-truncate px-1" style={{ fontSize: '13px' }}>{questionData.askedIn || 'N/A'}</div>
            </div>
          </CCol>
          <CCol xs={6} sm={4} md={3} lg={2}>
            <div className="border p-2 rounded bg-light shadow-xs h-100">
              <div className="text-muted small text-uppercase fw-bold mb-1" style={{ fontSize: '11px' }}>Asked Year</div>
              <div className="fw-bold text-warning" style={{ fontSize: '13px' }}>{questionData.year || questionData.askedYear || 'N/A'}</div>
            </div>
          </CCol>
          <CCol xs={6} sm={4} md={3} lg={2}>
            <div className="border p-2 rounded bg-light shadow-xs h-100">
              <div className="text-muted small text-uppercase fw-bold mb-1" style={{ fontSize: '11px' }}>Book Reference</div>
              <div className="fw-bold text-secondary text-truncate px-1" style={{ fontSize: '13px' }}>{questionData.bookReference || 'N/A'}</div>
            </div>
          </CCol>
          <CCol xs={6} sm={4} md={3} lg={2}>
            <div className="border p-2 rounded bg-light shadow-xs h-100">
              <div className="text-muted small text-uppercase fw-bold mb-1" style={{ fontSize: '11px' }}>Complexity</div>
              <div className="fw-bold text-success text-truncate px-1" style={{ fontSize: '13px' }}>{questionData.complexity || 'Easy'}</div>
            </div>
          </CCol>
        </CRow>
      </div>

      {/* Action Row */}
      <div className="bg-white border rounded p-3 shadow-sm mb-4">
        <CRow className="align-items-center g-3">
          <CCol xs={12} sm={6} className="d-flex align-items-center gap-2">
            <span className="fw-bold text-dark" style={{ fontSize: '0.95rem' }}>Social Media Icon to Share:</span>
            <div className="d-flex gap-2">
              <button onClick={() => handleShare('facebook')} className="btn btn-sm btn-outline-primary d-flex align-items-center justify-content-center" style={{ borderRadius: '50%', width: '32px', height: '32px', padding: '0' }}><FaFacebookF size={14} /></button>
              <button onClick={() => handleShare('whatsapp')} className="btn btn-sm btn-outline-success d-flex align-items-center justify-content-center" style={{ borderRadius: '50%', width: '32px', height: '32px', padding: '0' }}><FaWhatsapp size={15} /></button>
              <button onClick={() => handleShare('twitter')} className="btn btn-sm btn-outline-dark d-flex align-items-center justify-content-center" style={{ borderRadius: '50%', width: '32px', height: '32px', padding: '0' }}><FaXTwitter size={14} /></button>
            </div>
          </CCol>
          <CCol xs={12} sm={6} className="d-flex align-items-center gap-2 justify-content-sm-end">
            <span className="fw-bold text-dark" style={{ fontSize: '0.95rem' }}>Download this as PDF:</span>
            <button 
              onClick={handleDirectDownloadPDF} 
              disabled={downloadingPdf}
              className="btn btn-sm btn-danger py-2 px-3 fw-bold d-flex align-items-center gap-2 shadow-sm" 
              style={{ borderRadius: '6px' }}
            >
              {downloadingPdf ? '⏳ Downloading...' : '📄 Download PDF'}
            </button>
          </CCol>
        </CRow>
      </div>

      {/* Core Question Content */}
      <CCard className="shadow-sm border-0 mb-4">
        <CCardBody className="p-4 bg-white rounded border">
          <div className="mb-4">
            <h5 className="fw-bold text-primary d-flex align-items-center gap-2 mb-3">❓ Question Statement:</h5>
            <div className="p-3 bg-light rounded text-dark border-start border-primary border-4 shadow-xs overflow-hidden" style={{ fontSize: '1.2rem', lineHeight: '1.6' }}>
              <MathRenderer htmlContent={questionData.questionText || questionData.QuestionText} />
            </div>
          </div>

          {!isSubjectiveMode && (
            <div className="mb-5">
              <h5 className="fw-bold text-secondary d-flex align-items-center gap-2 mb-3">🎯 Options Array Matrix:</h5>
              <CRow className="g-3">
                {activeOptions.map((opt) => {
                  const colWidth = opt.key === 'optionE' ? 12 : 6;
                  return (
                    <CCol xs={12} md={colWidth} key={opt.key}>
                      <div className="d-flex align-items-center gap-2 border p-3 rounded bg-white shadow-xs w-100 h-100 overflow-hidden" style={{ borderLeft: '4px solid #ced4da' }}>
                        <span className="fw-bold text-primary px-1" style={{ fontSize: '1.1rem' }}>({opt.label})</span>
                        <div className="flex-grow-1 text-dark m-0 p-0 d-inline-block align-middle overflow-hidden" style={{ width: 'calc(100% - 35px)' }}>
                          {renderContent(opt.value)}
                        </div>
                      </div>
                    </CCol>
                  );
                })}
              </CRow>
            </div>
          )}

          <AnnouncementBanner position="between-q-sol" />

          {/* Solution Section */}
          <div className="mt-4 pt-3 border-top">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="fw-bold text-success m-0 d-flex align-items-center gap-2">💡 Step-by-Step Solution / Proof:</h5>
              <CButton color={showSolution ? "secondary" : "success"} size="sm" className="fw-bold text-white px-3 py-1.5 shadow-sm" onClick={handleToggleSolution}>
                {showSolution ? '🙈 Hide Solution' : '👁️ View Solution'}
              </CButton>
            </div>
            <CCollapse visible={showSolution}>
              <div className="p-3 rounded border-start border-success border-4 shadow-xs overflow-hidden" style={{ backgroundColor: '#f4faf6', minHeight: '100px', fontSize: '1.15rem' }}>
                {(questionData.solution || questionData.Solution) ? renderContent(questionData.solution || questionData.Solution) : <span className="text-muted italic">No structural solution logic submitted yet.</span>}
              </div>
            </CCollapse>
          </div>
        </CCardBody>
      </CCard>

      {/* TEMPORARY MOUNT CONTAINER FOR PDF GENERATION */}
      <div 
        id="pdf-render-wrapper"
        style={{ 
          position: 'absolute', 
          top: '-99999px', 
          left: '-99999px', 
          visibility: 'hidden', 
          opacity: 0,
          backgroundColor: '#ffffff'
        }}
      >
        <div 
          ref={pdfTemplateRef} 
          style={{ 
            width: '750px', 
            padding: '24px', 
            backgroundColor: '#ffffff', 
            color: '#333333', 
            fontFamily: 'Arial, sans-serif'
          }}
        >
          <div style={{ borderBottom: '2px solid #002147', paddingBottom: '12px', marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h2 style={{ color: '#002147', margin: 0, fontWeight: 'bold' }}>AMBIPIE LEARNING PORTAL</h2>
              <p style={{ color: '#666', margin: 0, fontSize: '12px' }}>Verified Academic Core Syllabus Resource Repository</p>
            </div>
            <div style={{ textAlign: 'right' }}>
              <strong style={{ fontSize: '14px', color: '#002147' }}>ID: {questionData?.customQuestionId || `LAAC${id}`}</strong>
              <br />
              <span style={{ fontSize: '12px', color: '#555' }}>Type: {questionData?.questionType || 'Objective'}</span>
            </div>
          </div>

          <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '20px', fontSize: '13px', backgroundColor: '#f9f9f9', border: '1px solid #ddd' }}>
            <tbody>
              <tr>
                <td style={{ padding: '8px', border: '1px solid #ddd', width: '50%' }}><strong>Chapter:</strong> {questionData?.chapterName || 'N/A'}</td>
                <td style={{ padding: '8px', border: '1px solid #ddd', width: '50%' }}><strong>Topic:</strong> {questionData?.topicName || 'N/A'}</td>
              </tr>
              <tr>
                <td style={{ padding: '8px', border: '1px solid #ddd' }}><strong>Exam Reference:</strong> {questionData?.askedIn || 'N/A'} - {questionData?.askedYear || questionData?.year || 'N/A'}</td>
                <td style={{ padding: '8px', border: '1px solid #ddd' }}><strong>Book Source:</strong> {questionData?.bookReference || 'N/A'}</td>
              </tr>
            </tbody>
          </table>

          <div style={{ marginBottom: '25px' }}>
            <h5 style={{ color: '#002147', fontWeight: 'bold', marginBottom: '10px' }}>QUESTION STATEMENT:</h5>
            <div id="pdf-question-stmt" style={{ fontSize: '15px', lineHeight: '1.6', paddingLeft: '10px', borderLeft: '3px solid #002147' }} />
          </div>

          {!isSubjectiveMode && (
            <div style={{ marginBottom: '25px' }}>
              <h5 style={{ color: '#002147', fontWeight: 'bold', marginBottom: '10px' }}>OPTIONS:</h5>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                {activeOptions.map(opt => (
                  <div key={opt.key} style={{ border: '1px solid #e0e0e0', borderRadius: '4px', padding: '10px', backgroundColor: '#fff', display: 'flex', gap: '6px' }}>
                    <strong style={{ color: '#002147' }}>({opt.label})</strong>
                    <div id={`pdf-opt-${opt.label}`} />
                  </div>
                ))}
              </div>
            </div>
          )}

          {(questionData?.solution || questionData?.Solution) && (
            <div style={{ marginTop: '30px', paddingTop: '15px', borderTop: '1px dashed #ccc' }}>
              <h5 style={{ color: '#198754', fontWeight: 'bold', marginBottom: '10px' }}>DETAILED ANSWER EXPLANATION / SOLUTION:</h5>
              <div id="pdf-solution-body" style={{ fontSize: '14px', lineHeight: '1.6', backgroundColor: '#f4faf6', padding: '12px', borderRadius: '4px', borderLeft: '3px solid #198754' }} />
            </div>
          )}
        </div>
      </div>

    </CContainer>
  );
};

export default ViewQuestion;